import * as Dialog from '@radix-ui/react-dialog';
import { compareGoals, createDeliverableSchema, createEpicSchema, createGoalSchema } from '@mona-roadmap/domain';
import type { CreateDeliverable, CreateEpic, CreateGoal, Epic, Metadata, ProductGoal } from '@mona-roadmap/domain';
import { ChevronDown, X } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { formatDate } from '../lib/format';
import { PeopleField, SelectField } from './cells/Editors';
import { Field } from './drawers/parts';
import type { OptionItem } from './ui/OptionList';
import { SingleSelect } from './ui/Pickers';

function DialogShell({ title, onClose, onSubmit, submitLabel, children, error }: { title: string; onClose: () => void; onSubmit: (e: FormEvent) => void; submitLabel: string; children: ReactNode; error: string | null }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content className="modal" aria-describedby={undefined} data-testid="create-dialog">
          <form onSubmit={onSubmit} noValidate>
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <Dialog.Title className="text-[17px] font-semibold">{title}</Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className="btn btn-ghost btn-icon" aria-label="Close">
                  <X className="size-4" />
                </button>
              </Dialog.Close>
            </div>
            <div className="space-y-4 px-4 py-4">
              {children}
              {error ? (
                <p className="text-[14px] text-danger" role="alert" data-testid="form-error">
                  {error}
                </p>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 border-t border-line px-4 py-3">
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {submitLabel}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function NameInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id}>
      <input id={id} autoFocus className="input !h-8" value={value} maxLength={255} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

function RelationField({ label, value, items, emptyText, onChange }: { label: string; value: string | null; items: OptionItem[]; emptyText: string; onChange: (v: string | null) => void }) {
  const current = items.find((i) => i.value === value);
  return (
    <Field label={label}>
      <SingleSelect
        label={label}
        items={items}
        value={value}
        searchable
        clearLabel={emptyText}
        onChange={onChange}
        trigger={
          <button type="button" className="cell-btn !m-0 !h-8 w-full !justify-between !border-line-strong !bg-app !px-2" aria-label={`${label}: ${current?.label ?? emptyText}. Change`}>
            <span className={`truncate ${current ? '' : 'italic text-faint'}`}>{current?.label ?? emptyText}</span>
            <ChevronDown className="size-3.5 text-faint" aria-hidden />
          </button>
        }
      />
    </Field>
  );
}

const NOKEY = 'form';

export function CreateDeliverableDialog({
  prefill,
  goals,
  epics,
  metadata,
  sprintColors,
  onClose,
  onSubmit,
}: {
  prefill: { productGoalId: string | null; epicId: string | null };
  goals: ProductGoal[];
  epics: Epic[];
  metadata: Metadata;
  sprintColors: Record<string, { bg: string; fg: string }>;
  onClose: () => void;
  onSubmit: (input: CreateDeliverable) => void;
}) {
  const [name, setName] = useState('');
  const [goalId, setGoalId] = useState<string | null>(prefill.productGoalId);
  const [epicId, setEpicId] = useState<string | null>(prefill.epicId);
  const [status, setStatus] = useState<string | null>(null);
  const [sprint, setSprint] = useState<string | null>(null);
  const [pm, setPm] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const goalItems = useMemo<OptionItem[]>(() => [...goals].sort(compareGoals).map((g) => ({ value: g.id, label: g.name, hint: g.targetDate ? formatDate(g.targetDate) : undefined })), [goals]);
  const epicItems = useMemo<OptionItem[]>(() => [...epics].sort((a, b) => a.position - b.position).map((e) => ({ value: e.id, label: e.name, hint: e.productArea ?? undefined })), [epics]);
  const personById = (ids: string[]) => ids.map((id) => metadata.people.find((p) => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const input: CreateDeliverable = {
      name: name.trim(),
      ...(goalId ? { productGoalId: goalId } : {}),
      ...(epicId ? { epicId } : {}),
      ...(status ? { status } : {}),
      ...(sprint ? { sprint } : {}),
      ...(pm.length ? { pmOwnerIds: pm } : {}),
      ...(assignees.length ? { assigneeIds: assignees } : {}),
    };
    const parsed = createDeliverableSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input');
      return;
    }
    onSubmit(parsed.data);
  };

  return (
    <DialogShell title="New Deliverable" onClose={onClose} onSubmit={submit} submitLabel="Create deliverable" error={error}>
      <NameInput label="Name" value={name} onChange={(v) => { setName(v); setError(null); }} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <RelationField label="Product Goal" value={goalId} items={goalItems} emptyText="No Product Goal" onChange={setGoalId} />
        <RelationField label="Epic" value={epicId} items={epicItems} emptyText="Standalone (no epic)" onChange={setEpicId} />
        <Field label="Status">
          <SelectField block lazy={false} field="Status" style="chip" value={status} options={metadata.options.status} stateKey={`${NOKEY}:status`} onChange={setStatus} clearLabel="No status" />
        </Field>
        <Field label="Sprint">
          <SelectField block lazy={false} field="Sprint" style="sprint" value={sprint} options={metadata.options.sprint} sprintColors={sprintColors} stateKey={`${NOKEY}:sprint`} onChange={setSprint} clearLabel="No sprint" />
        </Field>
        <Field label="PM Owner">
          <PeopleField block lazy={false} field="PM Owner" people={personById(pm)} all={metadata.people} stateKey={`${NOKEY}:pm`} onCommit={setPm} />
        </Field>
        <Field label="Assignee">
          <PeopleField block lazy={false} field="Assignee" people={personById(assignees)} all={metadata.people} stateKey={`${NOKEY}:asg`} onCommit={setAssignees} />
        </Field>
      </div>
    </DialogShell>
  );
}

export function CreateGoalDialog({ metadata, onClose, onSubmit }: { metadata: Metadata; onClose: () => void; onSubmit: (input: CreateGoal) => void }) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dateId = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = createGoalSchema.safeParse({ name: name.trim(), ...(target ? { targetDate: target } : {}), ...(status ? { status } : {}) });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Invalid input');
    onSubmit(parsed.data);
  };
  return (
    <DialogShell title="New Product Goal" onClose={onClose} onSubmit={submit} submitLabel="Create product goal" error={error}>
      <NameInput label="Name" value={name} onChange={(v) => { setName(v); setError(null); }} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Target date" htmlFor={dateId}>
          <input id={dateId} type="date" className="input !h-8" value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label="Status">
          <SelectField block lazy={false} field="Goal status" style="chip" value={status} options={metadata.options.pgStatus} stateKey={`${NOKEY}:gstatus`} onChange={setStatus} clearLabel="No status" />
        </Field>
      </div>
    </DialogShell>
  );
}

export function CreateEpicDialog({ metadata, onClose, onSubmit }: { metadata: Metadata; onClose: () => void; onSubmit: (input: CreateEpic) => void }) {
  const [name, setName] = useState('');
  const [area, setArea] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [pm, setPm] = useState<string[]>([]);
  const [rd, setRd] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const personById = (ids: string[]) => ids.map((id) => metadata.people.find((p) => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = createEpicSchema.safeParse({
      name: name.trim(),
      ...(area ? { productArea: area } : {}),
      ...(type ? { type } : {}),
      ...(pm.length ? { pmOwnerIds: pm } : {}),
      ...(rd.length ? { rdOwnerIds: rd } : {}),
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Invalid input');
    onSubmit(parsed.data);
  };
  return (
    <DialogShell title="New Epic" onClose={onClose} onSubmit={submit} submitLabel="Create epic" error={error}>
      <NameInput label="Name" value={name} onChange={(v) => { setName(v); setError(null); }} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Product Area">
          <SelectField block lazy={false} field="Product Area" style="text" value={area} options={metadata.options.productArea} stateKey={`${NOKEY}:area`} onChange={setArea} clearLabel="No product area" />
        </Field>
        <Field label="Type">
          <SelectField block lazy={false} field="Type" style="text" value={type} options={metadata.options.type} stateKey={`${NOKEY}:type`} onChange={setType} clearLabel="No type" />
        </Field>
        <Field label="PM Owner">
          <PeopleField block lazy={false} field="PM Owner" people={personById(pm)} all={metadata.people} stateKey={`${NOKEY}:pm`} onCommit={setPm} />
        </Field>
        <Field label="R&D owner">
          <PeopleField block lazy={false} field="R&D owner" people={personById(rd)} all={metadata.people} stateKey={`${NOKEY}:rd`} onCommit={setRd} />
        </Field>
      </div>
    </DialogShell>
  );
}
